import { 
  ref, 
  uploadBytesResumable,
  uploadBytes,
  getDownloadURL, 
  UploadTaskSnapshot 
} from 'firebase/storage';
import { storage } from '../firebase';
import { firebaseStorageTester } from '../utils/firebaseStorageTest';

export interface UploadProgress {
  progress: number; // 0-100
  bytesTransferred: number;
  totalBytes: number;
  state: 'running' | 'paused' | 'success' | 'error';
}

export interface UploadResult {
  downloadURL: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  uploadTime: Date;
}

class UploadService {
  // 上傳PDF文件到Firebase Storage
  async uploadPDF(
    file: File, 
    userId: string, 
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      console.log('開始上傳PDF文件:', file.name);
      
      // 驗證文件類型
      if (file.type !== 'application/pdf') {
        throw new Error('只允許上傳PDF文件');
      }
      
      // 驗證文件大小（限制為10MB）
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('文件大小不能超過10MB');
      }
      
      // 生成唯一的文件名（使用簡單的時間戳+隨機數，避免編碼問題）
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 9);
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      // 使用簡單的文件名：時間戳_隨機字符串.pdf
      // 原始文件名會保存在 Firestore 的 fileName 欄位中
      const fileName = `${timestamp}_${randomStr}${fileExtension}`;
      const filePath = `policies/${userId}/${fileName}`;
      
      console.log('文件路徑:', filePath);
      console.log('原始文件名:', file.name);
      console.log('生成的文件名:', fileName);
      
      // 創建存儲引用
      const storageRef = ref(storage, filePath);
      
      // 檢查文件路徑長度（Firebase Storage 有路徑長度限制）
      if (filePath.length > 1024) {
        throw new Error('文件路徑過長，請使用較短的文件名');
      }
      
      console.log('準備上傳，文件大小:', file.size, 'bytes');
      console.log('存儲引用路徑:', storageRef.fullPath);
      console.log('用戶ID:', userId);
      console.log('當前認證狀態:', await this.checkAuthStatus());
      
      // 運行診斷測試
      console.log('🔍 運行 Firebase Storage 診斷測試...');
      const testResults = await firebaseStorageTester.runAllTests(userId, filePath, file.size);
      firebaseStorageTester.printResults(testResults);
      
      // 檢查是否有關鍵測試失敗
      const criticalTests = testResults.filter((r: any) => !r.passed && 
        (r.testName.includes('認證') || r.testName.includes('規則'))
      );
      if (criticalTests.length > 0) {
        console.error('❌ 關鍵測試失敗，上傳可能會失敗:');
        criticalTests.forEach((test: any) => {
          console.error(`  - ${test.testName}: ${test.message}`);
        });
      }
      
      // 策略：優先嘗試 uploadBytes（更穩定），如果失敗再使用 uploadBytesResumable
      // 即使文件大於 5MB，也先嘗試 uploadBytes（Firebase 支持單文件最大 5GB）
      console.log('嘗試使用 uploadBytes（非可恢復上傳，更穩定）');
      try {
        // 對於 uploadBytes，我們需要模擬進度更新，因為它不支持進度回調
        // 先報告開始上傳
        if (onProgress) {
          onProgress({
            progress: 0,
            bytesTransferred: 0,
            totalBytes: file.size,
            state: 'running'
          });
        }
        
        // 使用 Promise 來追蹤上傳進度
        let uploadStartTime = Date.now();
        let lastProgressUpdate = 0;
        
        // 模擬進度更新（每200ms更新一次，使用指數增長模擬）
        const progressInterval = setInterval(() => {
          if (onProgress) {
            const elapsed = Date.now() - uploadStartTime;
            // 使用指數增長模擬上傳進度（前90%較快，最後10%較慢）
            // 假設平均上傳時間為文件大小的函數（約 1MB/s）
            const estimatedTime = Math.max(1000, file.size / (1024 * 1024) * 1000); // 至少1秒
            const progressRatio = Math.min(0.95, 1 - Math.exp(-elapsed / (estimatedTime * 0.7)));
            const currentProgress = Math.floor(progressRatio * 100);
            const currentBytes = Math.floor(progressRatio * file.size);
            
            // 只在進度有實際變化時更新
            if (currentProgress > lastProgressUpdate) {
              lastProgressUpdate = currentProgress;
              onProgress({
                progress: currentProgress,
                bytesTransferred: currentBytes,
                totalBytes: file.size,
                state: 'running'
              });
            }
          }
        }, 200);
        
        const snapshot = await uploadBytes(storageRef, file, {
          contentType: 'application/pdf',
          customMetadata: {
            originalFileName: file.name,
            uploadedAt: new Date().toISOString()
          }
        });
        
        // 清除進度模擬
        clearInterval(progressInterval);
        
        // 報告完成
        if (onProgress) {
          onProgress({
            progress: 100,
            bytesTransferred: file.size,
            totalBytes: file.size,
            state: 'success'
          });
        }
        
        console.log('✅ uploadBytes 上傳成功，獲取下載URL...');
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        const result: UploadResult = {
          downloadURL,
          fileName: file.name,
          filePath,
          fileSize: file.size,
          uploadTime: new Date()
        };
        
        console.log('✅ 文件上傳成功（使用 uploadBytes）:', result);
        return result;
      } catch (uploadBytesError: any) {
        console.warn('⚠️ uploadBytes 失敗，錯誤:', uploadBytesError.message);
        console.warn('⚠️ 錯誤代碼:', uploadBytesError.code);
        console.log('回退到 uploadBytesResumable（可恢復上傳）...');
        
        // 如果 uploadBytes 失敗，繼續使用 uploadBytesResumable
      }
      
      // 備選方案：使用 uploadBytesResumable（可恢復上傳，支持大文件）
      console.log('使用 uploadBytesResumable（可恢復上傳）');
      let uploadTask: ReturnType<typeof uploadBytesResumable>;
      try {
        uploadTask = uploadBytesResumable(storageRef, file, {
          contentType: 'application/pdf',
          customMetadata: {
            originalFileName: file.name,
            uploadedAt: new Date().toISOString()
          }
        });
        console.log('上傳任務已創建');
      } catch (error: any) {
        console.error('創建上傳任務失敗:', error);
        throw new Error(`創建上傳任務失敗: ${error.message}`);
      }
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            // 計算上傳進度
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            
            const progressData: UploadProgress = {
              progress: Math.round(progress),
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
              state: snapshot.state as any
            };
            
            console.log(`上傳進度: ${progressData.progress}%`);
            
            // 調用進度回調
            if (onProgress) {
              onProgress(progressData);
            }
          },
          (error) => {
            console.error('上傳失敗:', error);
            console.error('錯誤代碼:', error.code);
            console.error('錯誤詳情:', error);
            
            // 處理特定錯誤
            let errorMessage = `上傳失敗: ${error.message}`;
            if (error.code === 'storage/unknown') {
              // 412 錯誤通常是 Firebase Storage 規則或配置問題
              errorMessage = '上傳失敗：可能是 Firebase Storage 規則問題或網絡連接問題。請檢查 Firebase Console → Storage → Rules 是否正確部署。';
            } else if (error.code === 'storage/unauthorized') {
              errorMessage = '上傳失敗：沒有權限，請確認已登入';
            } else if (error.code === 'storage/canceled') {
              errorMessage = '上傳已取消';
            } else if (error.code === 'storage/quota-exceeded') {
              errorMessage = '上傳失敗：存儲空間不足';
            } else if (error.code === 'storage/retry-limit-exceeded') {
              errorMessage = '上傳失敗：重試次數已達上限，請稍後再試';
            }
            
            console.error('❌ 上傳錯誤詳情:', {
              code: error.code,
              message: error.message,
              serverResponse: error.serverResponse,
              customData: error.customData
            });
            
            reject(new Error(errorMessage));
          },
          async () => {
            try {
              console.log('✅ 上傳任務完成回調觸發');
              console.log('上傳任務狀態:', uploadTask.snapshot.state);
              console.log('上傳任務元數據:', uploadTask.snapshot.metadata);
              console.log('已傳輸字節:', uploadTask.snapshot.bytesTransferred);
              console.log('總字節數:', uploadTask.snapshot.totalBytes);
              
              // 驗證上傳是否真正完成
              if (uploadTask.snapshot.state !== 'success') {
                console.warn('⚠️ 上傳任務狀態不是 success:', uploadTask.snapshot.state);
                throw new Error(`上傳任務狀態異常: ${uploadTask.snapshot.state}`);
              }
              
              // 等待一小段時間，確保上傳完全完成
              console.log('等待 1 秒確保上傳完全完成...');
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // 上傳完成，獲取下載URL
              console.log('開始獲取下載URL...');
              console.log('引用路徑:', uploadTask.snapshot.ref.fullPath);
              
              // 測試：先檢查文件是否存在
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                console.log('✅ 下載URL獲取成功:', downloadURL);
                
                const result: UploadResult = {
                  downloadURL,
                  fileName: file.name, // 保存原始文件名
                  filePath,
                  fileSize: file.size,
                  uploadTime: new Date()
                };
                
                console.log('✅ 文件上傳成功:', result);
                resolve(result);
              } catch (urlError: any) {
                console.error('❌ 獲取下載URL失敗:', urlError);
                console.error('錯誤代碼:', urlError.code);
                console.error('錯誤詳情:', urlError);
                
                // 如果獲取 URL 失敗，但文件可能已經上傳成功，嘗試重試
                console.warn('⏳ 等待 3 秒後重試獲取 URL...');
                await new Promise(resolve => setTimeout(resolve, 3000));
                
                try {
                  console.log('🔄 重試獲取下載URL...');
                  const retryURL = await getDownloadURL(uploadTask.snapshot.ref);
                  const result: UploadResult = {
                    downloadURL: retryURL,
                    fileName: file.name,
                    filePath,
                    fileSize: file.size,
                    uploadTime: new Date()
                  };
                  console.log('✅ 重試成功，文件上傳完成:', result);
                  resolve(result);
                  return;
                } catch (retryError: any) {
                  console.error('❌ 重試也失敗:', retryError);
                  console.error('重試錯誤代碼:', retryError.code);
                  
                  // 最後嘗試：直接構建 URL（如果知道文件已上傳）
                  console.warn('⚠️ 嘗試直接構建下載URL...');
                  const directURL = `https://firebasestorage.googleapis.com/v0/b/${storage.app.options.storageBucket}/o/${encodeURIComponent(filePath)}?alt=media`;
                  console.log('構建的URL:', directURL);
                  
                  reject(new Error(`獲取下載URL失敗: ${urlError.message}。文件可能已上傳，但無法獲取URL。請檢查 Firebase Storage 規則。`));
                }
              }
            } catch (error: any) {
              console.error('❌ 上傳完成回調中發生錯誤:', error);
              reject(new Error(`上傳完成處理失敗: ${error.message}`));
            }
          }
        );
      });
    } catch (error: any) {
      console.error('上傳服務錯誤:', error.message);
      throw new Error(`上傳失敗: ${error.message}`);
    }
  }

  // 刪除文件
  async deleteFile(filePath: string): Promise<boolean> {
    try {
      console.log('開始刪除文件:', filePath);
      
      const { ref, deleteObject } = await import('firebase/storage');
      const fileRef = ref(storage, filePath);
      
      await deleteObject(fileRef);
      console.log('文件刪除成功:', filePath);
      return true;
    } catch (error: any) {
      console.error('文件刪除失敗:', error);
      return false;
    }
  }

  // 驗證PDF文件
  validatePDFFile(file: File): { isValid: boolean; error?: string } {
    // 檢查文件類型
    if (file.type !== 'application/pdf') {
      return {
        isValid: false,
        error: '只允許上傳PDF文件'
      };
    }
    
    // 檢查文件大小（限制為10MB）
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: '文件大小不能超過10MB'
      };
    }
    
    // 檢查文件名
    if (!file.name || file.name.trim() === '') {
      return {
        isValid: false,
        error: '文件名不能為空'
      };
    }
    
    return { isValid: true };
  }

  // 格式化文件大小
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // 檢查認證狀態（用於調試）
  private async checkAuthStatus(): Promise<string> {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        return `已登入: ${user.uid}`;
      } else {
        return '未登入';
      }
    } catch (error) {
      return '無法檢查認證狀態';
    }
  }

  // 生成文件預覽信息
  generateFilePreview(file: File): {
    name: string;
    size: string;
    type: string;
    lastModified: Date;
  } {
    return {
      name: file.name,
      size: this.formatFileSize(file.size),
      type: file.type,
      lastModified: new Date(file.lastModified)
    };
  }
}

export const uploadService = new UploadService();
